/**
 * ENVIRONMENT: PRODUCTION
 * MODULE: RuPay & PPI Prepaid Card Issuance Management (B2C)
 * PATH: src/routes/prepaid_cards/PrepaidCardList.js
 * COMPLIANCE: RBI Master Directions on PPIs (KYC & Card Issuance Lifecycle)
 */

import React, { useState, useEffect, useRef, useCallback } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  Table,
  Button,
  Space,
  Row,
  Col,
  Card,
  Tag,
  Tooltip,
  Typography,
  Input,
  Select,
  Drawer,
  Descriptions,
  message,
} from "antd";
import {
  SearchOutlined,
  ReloadOutlined,
  CreditCardOutlined,
  CheckCircleFilled,
  CloseCircleFilled,
  CopyOutlined,
  EyeOutlined,
} from "@ant-design/icons";
import _ from "lodash";
import { format } from "date-fns";
import { parseQueryParams, stringifyQueryParams } from "utils/url";
import { getPrepaidCardsList } from "requests/prepaid";
import "./PrepaidCardList.css";

const { Text, Title } = Typography;
const { Option } = Select;

function PrepaidCardList() {
  const [isTableLoading, setIsTableLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(10);
  const [totalCount, setTotalCount] = useState(0);
  const [records, setRecords] = useState([]);
  const [statusFilter, setStatusFilter] = useState("");
  const [searchTerm, setSearchTerm] = useState("");

  // Audit Detail Drawer State
  const [selectedCard, setSelectedCard] = useState(null);
  const [isDrawerVisible, setIsDrawerVisible] = useState(false);

  const location = useLocation();
  const navigate = useNavigate();

  const getData = useCallback(async (query) => {
    try {
      setIsTableLoading(true);
      const activeQuery = query || parseQueryParams(location);
      
      const response = await getPrepaidCardsList(activeQuery);

      if (response && response.data) {
        const resData = response.data;
        setRecords(resData.data || resData || []);
        setPage(Number(resData.current_page) || 1);
        setPerPage(Number(resData.per_page) || 10);
        setTotalCount(Number(resData.total) || 0);
      }
    } catch (err) {
      message.error(err?.response?.data?.message || "Failed to synchronize prepaid card ledger.");
      setRecords([]);
      setTotalCount(0);
    } finally {
      setIsTableLoading(false);
    }
  }, [location]);

  useEffect(() => {
    const query = parseQueryParams(location);
    if (query.status) setStatusFilter(query.status);
    if (query.keyword) {
      setSearchTerm(query.keyword);
    }
    getData(query);
  }, [location, getData]);

  const executeSearch = useCallback(
    (value) => {
      const trimmed = (value || "").trim();
      let query = parseQueryParams(location);
      query = { ...query, page: 1 };

      if (trimmed) {
        query.keyword = trimmed;
      } else {
        delete query.keyword;
      }

      navigate({
        pathname: location.pathname,
        search: stringifyQueryParams(query),
      });
    },
    [location, navigate]
  );

  const debouncedSearch = useRef(
    _.debounce((val) => {
      executeSearch(val);
    }, 450)
  ).current;

  const handleInputChange = (e) => {
    const val = e.target.value;
    setSearchTerm(val);
    debouncedSearch(val);
  };

  const copyToClipboard = (text, label) => {
    if (!text || text === "N/A") return;
    navigator.clipboard.writeText(text);
    message.success(`${label} copied to clipboard`);
  };

  const handleStatusChange = (val) => {
    setStatusFilter(val);
    let query = parseQueryParams(location);
    if (val) query = { ...query, page: 1, status: val };
    else delete query.status;
    navigate({ pathname: location.pathname, search: stringifyQueryParams(query) });
  };

  const onChangeTable = (pagination) => {
    let query = parseQueryParams(location);
    query = { ...query, page: pagination.current, per_page: pagination.pageSize };
    navigate({ pathname: location.pathname, search: stringifyQueryParams(query) });
  };

  const handleRefresh = () => {
    setSearchTerm("");
    setStatusFilter("");
    navigate({
      pathname: location.pathname,
      search: stringifyQueryParams({ page: 1 }),
    });
  };

  // Helper: Format genuine card issuance timestamp
  const formatIssuanceDate = (record) => {
    if (record.issued_date && record.issued_date !== "N/A") {
      try {
        return format(new Date(record.issued_date), "dd MMM yyyy, hh:mm a");
      } catch (e) {
        return record.issued_date;
      }
    }

    const rawDate = record.created_at || record.createdAt || record.date_time;
    if (rawDate) {
      try {
        return format(new Date(rawDate), "dd MMM yyyy, hh:mm a");
      } catch (e) {}
    }

    // Fallback: derive timestamp from MongoDB 24-character hexadecimal ObjectId
    const idStr = String(record._id || record.id || "");
    if (idStr.length === 24 && /^[0-9a-fA-F]{24}$/.test(idStr)) {
      try {
        const timestamp = parseInt(idStr.substring(0, 8), 16) * 1000;
        return format(new Date(timestamp), "dd MMM yyyy, hh:mm a");
      } catch (e) {}
    }

    return "Active Card";
  };

  const columns = [
    {
      title: "SR",
      width: 55,
      align: "center",
      render: (_, __, index) => (
        <span className="card-sr">{(page - 1) * perPage + index + 1}</span>
      ),
    },
    {
      title: "Card ID & Rail",
      width: 260,
      render: (_, record) => {
        const cardId = record.id || record.card_id || "N/A";
        const railLabel = record.card_rail || record.card_type || "RuPay VIRTUAL";

        return (
          <div style={{ display: "flex", flexDirection: "column", gap: 5 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
              <span
                style={{
                  fontFamily: "monospace",
                  fontWeight: 600,
                  color: "#0f172a",
                  background: "#f1f5f9",
                  padding: "3px 8px",
                  borderRadius: 6,
                  fontSize: 12,
                  wordBreak: "break-all",
                }}
              >
                {cardId}
              </span>
              {cardId !== "N/A" && (
                <Tooltip title="Copy Card UUID">
                  <CopyOutlined
                    style={{ cursor: "pointer", color: "#0284c7", fontSize: 13 }}
                    onClick={() => copyToClipboard(cardId, "Card ID")}
                  />
                </Tooltip>
              )}
            </div>
            <div>
              <Tag color="cyan" style={{ fontSize: 10.5, borderRadius: 4, margin: 0, fontWeight: 600 }}>
                {railLabel}
              </Tag>
            </div>
          </div>
        );
      },
    },
    {
      title: "Cardholder Details",
      width: 220,
      render: (_, record) => (
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <div
            style={{
              width: 34,
              height: 34,
              borderRadius: "50%",
              background: "#e0f2fe",
              color: "#0284c7",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              fontWeight: 700,
              fontSize: 13,
              flexShrink: 0,
            }}
          >
            {(record.holder_name || "U").charAt(0).toUpperCase()}
          </div>
          <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
            <span style={{ fontWeight: 600, color: "#0f172a", fontSize: 13 }}>
              {record.holder_name || "MTMPay User"}
            </span>
            {record.mobile ? (
              <span style={{ fontSize: 12, color: "#475569", fontFamily: "monospace" }}>
                +91 {record.mobile}
              </span>
            ) : (
              <span style={{ fontSize: 11, color: "#94a3b8" }}>Mobile Unlinked</span>
            )}
          </div>
        </div>
      ),
    },
    {
      title: "TBX Reference ID",
      width: 250,
      render: (_, record) => {
        const refId = record.reference_id || record.tbx_reference_id || "N/A";
        return (
          <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
            <span
              style={{
                fontFamily: "monospace",
                color: "#334155",
                background: "#f8fafc",
                border: "1px solid #e2e8f0",
                padding: "3px 8px",
                borderRadius: 6,
                fontSize: 12,
                wordBreak: "break-all",
              }}
            >
              {refId}
            </span>
            {refId !== "N/A" && (
              <Tooltip title="Copy TBX Reference">
                <CopyOutlined
                  style={{ cursor: "pointer", color: "#64748b", fontSize: 12 }}
                  onClick={() => copyToClipboard(refId, "Reference ID")}
                />
              </Tooltip>
            )}
          </div>
        );
      },
    },
    {
      title: "Sender Tag",
      width: 250,
      render: (_, record) => {
        const sender = record.sender_tag || record.sender_id || "N/A";
        return (
          <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
            <span
              style={{
                fontFamily: "monospace",
                color: "#0369a1",
                background: "#f0f9ff",
                border: "1px solid #bae6fd",
                padding: "3px 8px",
                borderRadius: 6,
                fontSize: 12,
                wordBreak: "break-all",
              }}
            >
              {sender}
            </span>
            {sender !== "N/A" && (
              <Tooltip title="Copy Sender Tag">
                <CopyOutlined
                  style={{ cursor: "pointer", color: "#0284c7", fontSize: 12 }}
                  onClick={() => copyToClipboard(sender, "Sender Tag")}
                />
              </Tooltip>
            )}
          </div>
        );
      },
    },
    {
      title: "Issued Date (IST)",
      width: 190,
      render: (_, record) => (
        <span style={{ fontSize: 12.5, color: "#475569", fontWeight: 500 }}>
          {formatIssuanceDate(record)}
        </span>
      ),
    },
    {
      title: "Status",
      width: 110,
      align: "center",
      render: (_, record) => {
        const isActive = (record.status || "").toUpperCase() === "ACTIVE";
        return (
          <Tag
            icon={isActive ? <CheckCircleFilled /> : <CloseCircleFilled />}
            color={isActive ? "success" : "error"}
            style={{ borderRadius: 6, fontWeight: 600, padding: "2px 8px" }}
          >
            {isActive ? "ACTIVE" : "BLOCKED"}
          </Tag>
        );
      },
    },
    {
      title: "Actions",
      width: 80,
      align: "center",
      render: (_, record) => (
        <Tooltip title="View Card Lifecycle Details">
          <Button
            type="text"
            icon={<EyeOutlined />}
            onClick={() => {
              setSelectedCard(record);
              setIsDrawerVisible(true);
            }}
            style={{ color: "#0284c7" }}
          />
        </Tooltip>
      ),
    },
  ];

  return (
    <div className="prepaid-card-container">
      {/* Top Header Card & Filter Matrix */}
      <Card className="prepaid-header-card" bordered={false}>
        <Row justify="space-between" align="middle" gutter={[16, 16]}>
          <Col xs={24} lg={9}>
            <div className="prepaid-title-wrap">
              <CreditCardOutlined className="header-card-icon" />
              <div>
                <Title level={4} className="prepaid-main-heading">
                  Prepaid Card Issuance (RuPay / PPI)
                </Title>
                <Text type="secondary" className="prepaid-subtext">
                  Complete lifecycle identifiers: Card ID, Reference ID, Sender Tag &amp; Issuance Logs
                </Text>
              </div>
            </div>
          </Col>

          <Col xs={24} lg={15} className="prepaid-controls-wrap">
            <Space wrap size="middle" className="controls-flex">
              <Select
                placeholder="All Card States"
                value={statusFilter || undefined}
                onChange={handleStatusChange}
                allowClear
                className="prepaid-status-select"
              >
                <Option value="ACTIVE">Active Only</Option>
                <Option value="BLOCKED">Blocked Only</Option>
              </Select>

              <Input
                placeholder="Search Card ID, Sender Tag, Name..."
                prefix={<SearchOutlined style={{ color: "#94a3b8" }} />}
                value={searchTerm}
                onChange={handleInputChange}
                onPressEnter={(e) => executeSearch(e.target.value)}
                allowClear
                className="prepaid-search-input"
              />

              <Button
                icon={<ReloadOutlined />}
                onClick={handleRefresh}
                className="prepaid-refresh-btn"
              />
            </Space>
          </Col>
        </Row>
      </Card>

      {/* Primary Card Ledger Table */}
      <Card className="prepaid-table-card" bordered={false}>
        <Table
          loading={isTableLoading}
          dataSource={records}
          columns={columns}
          onChange={onChangeTable}
          rowKey={(record) => record.id || record._id}
          pagination={{
            pageSize: perPage,
            total: totalCount,
            current: page,
            showSizeChanger: true,
            pageSizeOptions: ["10", "25", "50", "100"],
            showTotal: (total, range) =>
              `${range[0]}-${range[1]} of ${total} prepaid cards`,
          }}
          scroll={{ x: 1200 }}
          className="enterprise-prepaid-table"
        />
      </Card>

      {/* Card Audit Detail Drawer */}
      <Drawer
        title="Card Issuance Audit Receipt"
        placement="right"
        width={480}
        onClose={() => setIsDrawerVisible(false)}
        open={isDrawerVisible}
        visible={isDrawerVisible}
        className="card-audit-drawer"
      >
        {selectedCard && (
          <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                padding: "12px 16px",
                background: "#f8fafc",
                borderRadius: 8,
                border: "1px solid #e2e8f0",
              }}
            >
              <div>
                <span style={{ fontSize: 12, color: "#64748b" }}>Lifecycle State</span>
                <div style={{ marginTop: 4 }}>
                  <Tag
                    color={
                      (selectedCard.status || "").toUpperCase() === "ACTIVE"
                        ? "success"
                        : "error"
                    }
                    style={{ fontWeight: 600, borderRadius: 4 }}
                  >
                    {(selectedCard.status || "ACTIVE").toUpperCase()}
                  </Tag>
                </div>
              </div>
              <div style={{ textAlign: "right" }}>
                <span style={{ fontSize: 12, color: "#64748b" }}>Payment Rail</span>
                <div style={{ marginTop: 4 }}>
                  <Tag color="cyan" style={{ fontWeight: 600, borderRadius: 4 }}>
                    {selectedCard.card_rail || selectedCard.card_type || "RuPay VIRTUAL"}
                  </Tag>
                </div>
              </div>
            </div>

            <Descriptions
              title="Card Identification"
              column={1}
              bordered
              size="small"
            >
              <Descriptions.Item label="Card ID (UUID)">
                <Text copyable>{selectedCard.id || selectedCard.card_id || "N/A"}</Text>
              </Descriptions.Item>
              <Descriptions.Item label="TBX Reference ID">
                <Text copyable>{selectedCard.reference_id || selectedCard.tbx_reference_id || "N/A"}</Text>
              </Descriptions.Item>
              <Descriptions.Item label="Sender Tag">
                <Text copyable>{selectedCard.sender_tag || selectedCard.sender_id || "N/A"}</Text>
              </Descriptions.Item>
              <Descriptions.Item label="Issuance Timestamp">
                {formatIssuanceDate(selectedCard)}
              </Descriptions.Item>
            </Descriptions>

            <Descriptions
              title="Cardholder & Compliance"
              column={1}
              bordered
              size="small"
            >
              <Descriptions.Item label="Cardholder Name">
                {selectedCard.holder_name || "MTMPay User"}
              </Descriptions.Item>
              <Descriptions.Item label="Linked Mobile">
                {selectedCard.mobile ? `+91 ${selectedCard.mobile}` : "Unlinked"}
              </Descriptions.Item>
              <Descriptions.Item label="Card Form Factor">
                Virtual PPI Card (RuPay)
              </Descriptions.Item>
              <Descriptions.Item label="SDK Balance Verification">
                <Text type="secondary" style={{ fontSize: 12 }}>
                  Managed directly on client device via Card SDK view.
                </Text>
              </Descriptions.Item>
            </Descriptions>
          </div>
        )}
      </Drawer>
    </div>
  );
}

export default PrepaidCardList;